import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { SurfaceCard } from "@/components/common/Cards";
import { StatusBadge } from "@/components/common/StatusBadge";
import { formatDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, Search, Sparkles, ChevronRight } from "lucide-react";

export default function Reports() {
  const navigate = useNavigate();
  const [reports, setReports] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get("/reports").then((r) => setReports(r.data)).catch(() => setReports([])); }, []);

  const filtered = (reports || []).filter((r) => {
    const matchQ = r.title.toLowerCase().includes(q.toLowerCase());
    const matchF = filter === "all" || r.status === filter;
    return matchQ && matchF;
  });

  return (
    <div>
      <PageHeader title="Clinical Reports" subtitle="AI-drafted, clinician-approved clinical documents" breadcrumb={["Clinical", "Clinical Reports"]} />
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8B9698]" />
          <Input data-testid="report-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reports..." className="pl-9 h-10" />
        </div>
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList><TabsTrigger value="all">All</TabsTrigger><TabsTrigger value="review_required">In Review</TabsTrigger><TabsTrigger value="approved">Approved</TabsTrigger></TabsList>
        </Tabs>
      </div>

      {reports && filtered.length === 0 ? (
        <EmptyState icon={FileText} title="No reports available yet" description="Reports generated from completed clinical workflows will appear here." testId="reports-empty" />
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <SurfaceCard key={r.id} className="p-5 cursor-pointer hover:border-[#00A85A]/40" data-testid={`report-card-${r.id}`} onClick={() => navigate(`/reports/${r.id}`)}>
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E8F7F0] dark:bg-[#00A85A]/15"><FileText className="h-5 w-5 text-[#00A85A]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><div className="text-sm font-bold text-[#101516] dark:text-white truncate">{r.title}</div>{r.ai_generated && <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold text-[#00A85A]"><Sparkles className="h-3 w-3" />AI</span>}</div>
                  <div className="text-xs text-[#8B9698]">{r.type} · {r.patient_name} · {formatDate(r.created_at)}</div>
                </div>
                <StatusBadge status={r.status} />
                <ChevronRight className="h-4 w-4 text-[#8B9698]" />
              </div>
            </SurfaceCard>
          ))}
        </div>
      )}
    </div>
  );
}
